import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Building2, CheckCircle2, ChevronLeft, Key, Loader2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { PRACTICE_JURISDICTIONS } from "@/constants/practice";

// ============ GOVERNMENT RESPONSE PAGE ============

const GOVERNMENT_RESPONSE_TYPES = [
  { value: "foia", label: "FOIA/Public Records Request", desc: "Freedom of Information requests" },
  { value: "agency_response", label: "Agency Response Letter", desc: "Respond to government inquiries" },
  { value: "regulatory_comment", label: "Regulatory Comment", desc: "Comment on proposed rules" },
  { value: "administrative_appeal", label: "Administrative Appeal", desc: "Appeal agency decisions" },
  { value: "subpoena_response", label: "Subpoena Response", desc: "Respond to government subpoenas" },
  { value: "agency_complaint", label: "Agency Complaint", desc: "File complaints with agencies" }
];

const GovernmentResponsePage = () => {
  const [form, setForm] = useState({
    response_type: 'foia',
    jurisdiction: 'us_federal',
    agency_name: '',
    matter_description: '',
    client_position: 'requestor',
    deadline: '',
    key_issues: [],
    supporting_facts: [],
    relief_sought: ''
  });
  const [newIssue, setNewIssue] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const addIssue = () => { if (newIssue.trim()) { setForm(f => ({...f, key_issues: [...f.key_issues, newIssue.trim()]})); setNewIssue(''); }};
  
  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/government/generate`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="government-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Government Response</h1>
              <p className="text-muted-foreground">FOIA requests, agency responses, appeals & complaints</p>
            </div>
          </div>
        </div>
        
        {!result ? (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <Card>
                <CardHeader><CardTitle>Response Details</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Response Type</Label>
                      <Select value={form.response_type} onValueChange={v => setForm(f => ({...f, response_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {GOVERNMENT_RESPONSE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Jurisdiction</Label>
                      <Select value={form.jurisdiction} onValueChange={v => setForm(f => ({...f, jurisdiction: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PRACTICE_JURISDICTIONS.map(j => <SelectItem key={j.value} value={j.value}>{j.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Agency Name</Label>
                      <Input placeholder="e.g., Department of Justice, SEC" value={form.agency_name} onChange={e => setForm(f => ({...f, agency_name: e.target.value}))} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Client Position</Label>
                      <Select value={form.client_position} onValueChange={v => setForm(f => ({...f, client_position: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="requestor">Requestor</SelectItem>
                          <SelectItem value="respondent">Respondent</SelectItem>
                          <SelectItem value="appellant">Appellant</SelectItem>
                          <SelectItem value="complainant">Complainant</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Matter Description</Label>
                    <Textarea placeholder="Describe the matter, records sought, or issues..." value={form.matter_description} onChange={e => setForm(f => ({...f, matter_description: e.target.value}))} rows={4} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Key Issues</Label>
                    <div className="flex gap-2">
                      <Input placeholder="Add issue..." value={newIssue} onChange={e => setNewIssue(e.target.value)} onKeyDown={e => e.key === 'Enter' && addIssue()} />
                      <Button onClick={addIssue} variant="outline">Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {form.key_issues.map((issue, i) => <Badge key={i} variant="secondary">{issue} <button onClick={() => setForm(f => ({...f, key_issues: f.key_issues.filter((_, idx) => idx !== i)}))} className="ml-1">×</button></Badge>)}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Relief Sought</Label>
                    <Input placeholder="e.g., Production of records, reversal of decision" value={form.relief_sought} onChange={e => setForm(f => ({...f, relief_sought: e.target.value}))} />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-gradient-to-br from-slate-700 to-slate-900 text-white border-0">
              <CardContent className="p-6">
                <Button className="w-full h-14 text-lg gap-2 bg-white text-slate-700 hover:bg-white/90" onClick={generate} disabled={loading || !form.matter_description}>
                  {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Generating...</> : <><Building2 className="h-5 w-5" /> Generate Document</>}
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            <Card className="bg-gradient-to-r from-slate-700 to-slate-900 text-white border-0">
              <CardContent className="p-6">
                <h2 className="text-2xl font-bold">{result.response_name}</h2>
                <p className="text-white/80">{result.agency_name} • {result.jurisdiction}</p>
                <Button variant="outline" onClick={() => setResult(null)} className="mt-4 text-white border-white/20">New Document</Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="prose prose-sm max-w-none whitespace-pre-wrap">{result.content}</div>
              </CardContent>
            </Card>
            
            {result.filing_tips && (
              <Card>
                <CardHeader><CardTitle>Filing Tips</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1">{result.filing_tips.map((tip, i) => <li key={i} className="text-sm flex gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />{tip}</li>)}</ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default GovernmentResponsePage;
